export type ProjectMetric = {
  label: string;
  value: string;
};

export type Project = {
  slug: string;
  title: string;
  category: string;
  summary: string;
  description: string;
  tags: string[];
  outcome: string;
  year: number;
  status: 'Live' | 'Concept' | 'Case study';
  problem: string;
  approach: string;
  highlights: string[];
  metrics?: ProjectMetric[];
  link?: string;
};

export const projects: Project[] = [
  {
    slug: 'pharmavision-analytics',
    title: 'PharmaVision — Analytics',
    category: 'Pharma SaaS · Analytics',
    summary:
      'Real-time sales performance analytics for pharmaceutical companies — territories, products, and reps in one place.',
    description:
      'The analytics module of PharmaVision (pharmavision.io). Customizable dashboards, multi-dimensional tracking across products, territories, and regions, target vs. achievement visualization, and wholesaler/distributor monitoring — replacing weeks of manual reporting with minutes.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'MySQL'],
    outcome:
      'Cuts pharma reporting cycles from weeks to minutes; powers performance reviews across multiple enterprise pharma teams.',
    year: 2025,
    status: 'Live',
    link: 'https://pharmavision.io',
    problem:
      'Pharma sales leaders were running performance reviews out of exports from three different systems — wholesaler data in one place, rep activity in another, targets in a spreadsheet. Pulling a single quarterly view took weeks, and by the time it was ready the numbers were already stale. Regional managers ended up making decisions on gut feel instead of evidence.',
    approach:
      'We modeled the entire pharma sales hierarchy — products, territories, regions, reps — as one unified data layer, then layered a dashboard on top that lets a regional manager and a country director look at the same number from different angles without anyone exporting anything. The backend ingests wholesaler and distributor feeds on a schedule; the frontend renders customizable widgets so each role configures the view they actually need. Targets and achievements live in the same model, so trend indicators are computed, not eyeballed.',
    highlights: [
      'Customizable real-time dashboards per role (rep, regional manager, country lead)',
      'Multi-dimensional tracking: product × territory × region × time',
      'Target vs. achievement visualization with computed trend indicators',
      'Wholesaler and distributor monitoring with scheduled ingestion',
      'Audit-friendly data lineage so every number can be traced back to its source',
    ],
    metrics: [
      { label: 'Reporting cycle', value: 'Weeks → Minutes' },
      { label: 'Enterprise pharma teams', value: 'Multiple' },
      { label: 'Live since', value: '2025' },
    ],
  },
  {
    slug: 'pharmavision-crm',
    title: 'PharmaVision — CRM',
    category: 'Pharma SaaS · CRM',
    summary:
      'A CRM purpose-built for pharma field teams — HCP profiles, visit history, segmentation, and quarterly engagement planning.',
    description:
      'The CRM module of PharmaVision (pharmavision.io). 360° healthcare professional profiles with specialties and preferences, visit history and engagement frequency, pharmacy and institution relationship management, smart segmentation, and strategic quarterly planning for sales reps and regional managers.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'MySQL'],
    outcome:
      'Replaces fragmented spreadsheets and generic CRMs with a pharma-native workflow; saves reps up to 10 hours of admin per week.',
    year: 2025,
    status: 'Live',
    link: 'https://pharmavision.io',
    problem:
      'Generic CRMs treat every contact like a B2B lead. Pharma reps don\'t work that way — they manage long-running relationships with healthcare professionals, navigate institutional hierarchies, and plan visits in quarterly cycles bound by compliance rules. Off-the-shelf tools forced reps to bend their workflow to the software, and the result was an unloved CRM, half-empty fields, and managers with no real visibility.',
    approach:
      'We built the CRM around the rep\'s real day: a 360° HCP profile with specialties and preferences, visit history that\'s actually pleasant to update, smart segmentation that surfaces who to see next, and quarterly engagement planning baked into the same surface. Pharmacies and institutions get first-class records — not just shoved into a "company" field. The CRM module talks to the Analytics module through a shared data layer, so visit activity flows into territory-level performance views without anyone re-keying anything.',
    highlights: [
      '360° healthcare professional profiles with specialties and preferences',
      'Visit history and engagement frequency tracked per HCP and per institution',
      'Smart segmentation and categorization tools',
      'Quarterly engagement planning surface used by reps and managers',
      'Tight integration with the Analytics module — visits feed performance views',
      'Granular permissions per region, team, and role',
    ],
    metrics: [
      { label: 'Admin time saved per rep', value: '~10 hrs / week' },
      { label: 'Pharma teams using it', value: 'Multiple' },
      { label: 'Live since', value: '2025' },
    ],
  },
  {
    slug: 'nomadjobs',
    title: 'NomadJobs.cloud',
    category: 'Recruitment SaaS',
    summary:
      'A private recruitment management platform for job agencies — candidate documents, automated paperwork, status tracking, and company access in one system.',
    description:
      'A purpose-built recruitment platform for job agencies (NomadJobs.cloud, currently on version 2.0). Secure candidate document storage replaces paper filing entirely. Automated document generation produces contracts, offers, and other paperwork on demand. Candidate status management keeps agency teams in sync with one-click updates. Company user accounts let employer organizations view and manage candidates who applied directly to them, with refined search scoped to their own applicants. Admins get advanced filtering across the entire candidate base, while a granular roles-and-permissions system keeps each agency, company, and team member scoped to exactly what they should see.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'MySQL', 'RBAC'],
    outcome:
      'Replaces filing cabinets and manual contract drafting with a single platform; v2.0 added company access and advanced filtering, scaling the system from agency-only to agencies + their client companies.',
    year: 2024,
    status: 'Live',
    link: 'https://nomadjobs.cloud',
    problem:
      'Job agencies were drowning in paperwork. Candidate files lived in filing cabinets and shared drives, contracts were drafted by hand for every placement, and statuses lived in someone\'s inbox. When an employer company asked "who applied to us last week?" the answer required a phone call and a half-day of digging.',
    approach:
      'We built a private, agency-grade platform with two ideas at its core: every candidate has one secure record, and every document that the agency would ever generate is templated and produced automatically. Then in v2.0 we extended the model to the employer side — companies got their own scoped accounts so they can see exactly the candidates that applied to them, with search restricted to their own applicants. The roles-and-permissions system grew with each new actor type (admin, agency, company, candidate) without rewrites, because it was modeled from day one.',
    highlights: [
      'Secure candidate document storage — replaces paper filing cabinets',
      'Automated document generation: contracts, offers, agency paperwork',
      'One-click candidate status management across the agency team',
      'Company user accounts with applicant-scoped search and management (v2.0)',
      'Advanced admin filtering across the entire candidate base',
      'Granular RBAC: agency, company, team-member, candidate — each correctly scoped',
    ],
    metrics: [
      { label: 'Current version', value: '2.0' },
      { label: 'Actor types supported', value: 'Admin · Agency · Company · Candidate' },
      { label: 'Live since', value: '2024' },
    ],
  },
  {
    slug: 'fynvo',
    title: 'Fynvo',
    category: 'Invoicing & Inventory SaaS',
    summary:
      'Manage invoices the easy way — create, send, and track invoices, quotes, and proformas, with inventory and Shopify sales unified in one platform.',
    description:
      'Cloud SaaS available at fynvo.cloud. Professional invoicing with branded templates, PDF export, and email delivery. Expense tracking with recurring expenses and categories. Full inventory management — stock, packages, warehouse overview. Analytics with multi-currency revenue insights. Shopify integration brings online store sales into the same warehouse and accounting flow as direct sales from the client\'s own websites — a single source of truth for finance, stock, and orders.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'MySQL', 'Shopify API'],
    outcome:
      'One platform for invoicing, inventory, expenses, and Shopify sales — eliminates the spreadsheet-and-three-tools workflow most small businesses end up stuck with.',
    year: 2025,
    status: 'Live',
    link: 'https://fynvo.cloud',
    problem:
      'Small businesses selling online end up running invoicing in one tool, inventory in a spreadsheet, expenses in a notebook, and Shopify sales in a separate dashboard. By the time anyone has a clear picture of margin or stock, the month is half over and the picture is wrong. Reconciling all of it manually consumes more time than running the actual business.',
    approach:
      'We built Fynvo around a single principle: every transaction — invoice, expense, Shopify order, direct sale — moves the same shared model of stock and money. Invoicing produces beautiful, branded PDFs and emails. Inventory updates in real time as sales close. The Shopify integration brings online orders into the same flow as direct sales from the client\'s own websites, so warehouse and accounting see one truth. Multi-currency is supported throughout, not bolted on.',
    highlights: [
      'Professional invoices, quotes, and proformas with branded templates and PDF export',
      'Email delivery and tracking out of the box',
      'Expense tracking with recurring expenses and categories',
      'Real-time inventory: stock, packages, warehouse overview',
      'Shopify integration unifies online and direct sales',
      'Multi-currency analytics with revenue insights',
    ],
    metrics: [
      { label: 'Channels unified', value: 'Direct + Shopify' },
      { label: 'Currencies', value: 'Multi-currency' },
      { label: 'Live since', value: '2025' },
    ],
  },
  {
    slug: 'pharmacy-faculty-payments',
    title: 'Pharmacy Faculty Skopje — Online Payments',
    category: 'Payment Integration',
    summary:
      'Integrated an electronic payment gateway into the existing site of the Faculty of Pharmacy in Skopje.',
    description:
      'Added secure online payments to the faculty\'s existing website without rewriting it — integration with the payment provider, server-side transaction handling, receipts, and a reconciliation flow the administrative staff can actually use.',
    tags: ['PHP', 'Laravel', 'Payment Gateway', 'MySQL'],
    outcome:
      'Students and applicants can now pay fees online; manual cash/bank-slip handling at the faculty office was significantly reduced.',
    year: 2024,
    status: 'Live',
    problem:
      'The Faculty of Pharmacy in Skopje had an existing public website but no way to accept fees online. Students and applicants had to physically visit the office, get a bank slip, walk to a bank, pay, return with the receipt, and queue again to hand it in. Administrative staff then had to manually match each receipt to a student record. The bottleneck cost everyone time.',
    approach:
      'We integrated an electronic payment gateway directly into the existing site — no rewrite, no migration, no disruption to a system that was working fine for everything else. Server-side handling ensures every transaction is verified before it\'s recorded; receipts are generated automatically; and the reconciliation flow was designed with the actual administrative staff who use it daily, so it fits how they already think about student records.',
    highlights: [
      'Payment-gateway integration directly into the existing site, no rewrite needed',
      'Server-side transaction verification and audit log',
      'Automatic receipt generation and delivery',
      'Reconciliation flow designed with the administrative staff',
      'Significant reduction in counter visits and manual handling',
    ],
    metrics: [
      { label: 'Bank-slip handling', value: 'Dramatically reduced' },
      { label: 'Site rewrite required', value: 'No — added to existing' },
      { label: 'Live since', value: '2024' },
    ],
  },
  {
    slug: 'municipal-os',
    title: 'MunicipalOS',
    category: 'Utility ERP',
    summary:
      'A full management system for municipal utility companies — billing, cash desk, field collectors, customer requests, and admin analytics in one platform.',
    description:
      'End-to-end ERP for a komunalno pretprijatie (municipal utility operator). Customer side: bills, transactions, quick payments, meter readings, service requests, defect reporting, and announcements. Admin side: users and employees, configurable pricing with full history, interest on overdue accounts, bank accounts and statement reconciliation, service requests handling, printable bills, new-connection workflow, and analytics. Field operations: a dedicated inkasant (collector) role with task assignment from admins, daily reports, and per-collector stats. Granular role-based access keeps citizens, cashiers, collectors, and administrators each scoped to exactly what they should see.',
    tags: ['PHP', 'Laravel', 'React', 'TypeScript', 'MySQL', 'RBAC'],
    outcome:
      'Replaces a patchwork of spreadsheets, paper receipts, and disconnected tools with one system covering billing, cash, collections, customer self-service, and field-team coordination.',
    year: 2025,
    status: 'Live',
    problem:
      'A municipal utility company was running billing in one program, customer service over the phone and paper forms, field collection by handing inkasanti a list every morning, and reconciliation by manually matching bank statements to bill IDs. Overdue accounts were tracked in a spreadsheet that lived on one PC. There was no shared view of what each employee actually did during a day, and citizens had no self-service option for anything.',
    approach:
      'We designed MunicipalOS as a single platform with three faces: a citizen portal, an admin back-office, and a field-operations module for inkasanti. Pricing has full history and configurable interest on overdue accounts. Bank statements are imported and reconciled in-system. Inkasanti receive tasks directly from admins, submit daily reports, and get per-collector statistics that managers actually look at. Granular RBAC scopes each role tightly so a cashier never sees what an inkasant sees, and citizens only ever see their own account.',
    highlights: [
      'Citizen portal: bills, transactions, quick payments, meter readings, requests, defect reports, announcements',
      'Admin back-office: users, employees, pricing with full history, overdue interest, bank reconciliation, printable bills, new-connection workflow',
      'Inkasant (field collector) module: task assignment from admins, daily reports, per-collector stats',
      'Granular RBAC across citizens, cashiers, collectors, and admins',
      'Analytics dashboard covering billing, collection, and field-team performance',
    ],
    metrics: [
      { label: 'Roles supported', value: 'Citizen · Cashier · Inkasant · Admin' },
      { label: 'Workflow areas unified', value: 'Billing · Cash · Collections · Service · Field' },
      { label: 'Live since', value: '2025' },
    ],
  },
  {
    slug: 'pharmacy-os',
    title: 'PharmacyOS',
    category: 'Pharmacy Management Platform',
    summary:
      'A modern operating system for retail pharmacies — point-of-sale, prescription workflow, inventory, and a direct line to the national health insurance fund.',
    description:
      'A next-generation pharmacy management platform, currently in active development. Designed to replace the legacy software most pharmacies still run on, with a clean UI built for everyday counter work and a backend built for accountability. Fast POS for over-the-counter sales, prescription handling with electronic verification, and a direct integration with the national Health Insurance Fund so reimbursable prescriptions clear without paper shuffling. Inventory is live — every sale moves stock, every delivery updates it, and expiring batches surface before they cost the pharmacy money. Owners get real visibility into margins, fast-movers, and what each shift actually did.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'TypeScript', 'MySQL', 'POS', 'HIF Integration'],
    outcome:
      'Brings pharmacy retail, prescription dispensing, inventory, and health-fund reporting into a single modern system — no more switching between three tools and a paper ledger.',
    year: 2025,
    status: 'Concept',
    problem:
      'Most retail pharmacies in the region still run on legacy software that was built before health-fund electronic verification existed. POS, prescription dispensing, inventory, and HIF reimbursement live in different programs that don\'t talk to each other. The counter staff alt-tabs between systems, expiring stock surfaces too late, and the owner has no real-time view of margin.',
    approach:
      'PharmacyOS is being built as one modern system that covers the entire counter-to-fund flow. A fast POS for OTC sales sits on top of the same inventory model that the prescription workflow uses. Prescriptions are dispensed and electronically verified against the national Health Insurance Fund inline — no separate program, no paperwork. Stock is live: sales decrement it, deliveries increment it, and the system surfaces expiring batches early enough for the pharmacy to actually act on them. Owners get real margin visibility and per-shift accountability.',
    highlights: [
      'Fast OTC point-of-sale built for counter speed',
      'Prescription dispensing with electronic verification against the national HIF',
      'Real-time inventory: sales, deliveries, batches, expirations',
      'Margin visibility and fast-mover analytics for owners',
      'Per-shift activity log for accountability',
    ],
    metrics: [
      { label: 'Status', value: 'In active development' },
      { label: 'Target', value: 'Replace legacy pharmacy software' },
      { label: 'Launch', value: '2025' },
    ],
  },
  {
    slug: 'hotel-os',
    title: 'HotelOS',
    category: 'Hotel Management Platform',
    summary:
      'A complete hotel management system — rooms, restaurants, bars, POS terminals, inventory, and OTA integrations like Booking in one platform.',
    description:
      'End-to-end hospitality platform currently in active development. Room management with availability, rates, and stays. Full F&B operations covering the hotel\'s restaurants and bars — menus, orders, and POS terminals connected directly to inventory. Stock tracking across kitchens, bars, and storerooms so beverage and food levels stay in sync with what gets sold at the POS. Channel integrations with Booking and other OTAs to keep room inventory and reservations consistent across direct and third-party bookings.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'TypeScript', 'MySQL', 'POS', 'Booking API'],
    outcome:
      'Replaces the typical patchwork of PMS + separate POS + manual inventory spreadsheets with one system covering front desk, F&B, stock, and channel distribution.',
    year: 2025,
    status: 'Concept',
    problem:
      'Hotels typically run a property-management system, a separate POS for each restaurant and bar, an inventory spreadsheet for stock, and an OTA channel manager — four systems that need to be reconciled by hand. The result is stock counts that drift, double-booked rooms, and a finance picture that\'s always a week behind.',
    approach:
      'HotelOS is being built as a single platform where rooms, F&B, inventory, and channel distribution share one data model. Every POS sale in a restaurant or bar moves stock in the same inventory the storeroom sees. Room inventory is synced with Booking and other OTAs so direct and third-party reservations can\'t conflict. The front desk, F&B managers, and the owner all look at the same numbers — no reconciliation, no spreadsheets.',
    highlights: [
      'Room management: availability, rates, stays, reservations',
      'F&B operations: menus, orders, POS terminals for restaurants and bars',
      'Stock tracking across kitchens, bars, and storerooms — live with POS',
      'OTA channel integrations starting with Booking',
      'Unified data model — one source of truth across front desk, F&B, and stock',
    ],
    metrics: [
      { label: 'Status', value: 'In active development' },
      { label: 'Channels', value: 'Direct + OTA (Booking)' },
      { label: 'Launch', value: '2025' },
    ],
  },
  {
    slug: 'gna-events',
    title: 'GNA — Events Tracking',
    category: 'Internal Tool · Marketing',
    summary:
      'A custom events-management platform for GNA, a marketing agency that needed more than a generic spreadsheet workflow.',
    description:
      'End-to-end event tracking tailored to GNA\'s operations: event records, schedules, participants, deliverables, and reporting. Designed directly with the agency team so the daily workflow shaped the product.',
    tags: ['PHP', 'Laravel', 'Apiato', 'React', 'MySQL'],
    outcome:
      'Replaced scattered spreadsheets and email threads with a single source of truth for every campaign event the agency runs.',
    year: 2024,
    status: 'Live',
    problem:
      'GNA, a marketing agency, was running campaign events out of a moving target of spreadsheets, calendar invites, and email threads. With multiple events per campaign and multiple campaigns running in parallel, finding the latest schedule or participant list usually meant interrupting whoever owned the file.',
    approach:
      'We sat with the agency team and modeled their workflow as it actually was: events belong to campaigns, campaigns have participants and deliverables, deliverables have owners and deadlines. Everything that used to live in five different documents now lives in one record per event, accessible to whoever needs it. Reporting was designed against the questions the agency actually asks at the end of a campaign — not a generic dashboard.',
    highlights: [
      'Event records with schedules, participants, and deliverables',
      'Campaign-level grouping with cross-event reporting',
      'Single source of truth replaces spreadsheets and email threads',
      'Designed directly with the GNA team — workflow shaped the product',
    ],
    metrics: [
      { label: 'Tools replaced', value: 'Spreadsheets + email threads' },
      { label: 'Live since', value: '2024' },
    ],
  },
];
